import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './brickList.scss'
import { inject, observer } from 'mobx-react';
import ArrowSmallIcon from '../../icons/arrowSmallIcon';
import { decodeHTML, decodeRtfText } from '../../../helpers/html-helper';
import { ArticleState, IInjectedProps } from '../../../types';
import TabNavigationHelper from '../../../helpers/tabNavigationHelper';
import { when } from 'mobx';

function DetailsSelectButton({ isSelected, detailsButtonLabel }: IDetailsSelectButton) {
    return (
        <div className={classNames(styles.details_select_button, { [styles.selected]: isSelected })}>
            <ArrowSmallIcon />
            <span>{decodeHTML(detailsButtonLabel)}</span>
        </div>
    )
}

interface IDetailsSelectButton {
    isSelected: boolean;
    detailsButtonLabel: string
}

const BrickListOptions = React.memo(
    function BrickListOptions({ brickItemsAmount, productsAvailableLabel, disabled }: IBrickListOptions) {
        const [isMounted, setIsMounted] = useState(false);
        useEffect(() => { setIsMounted(true); }, []);
        if (!isMounted || brickItemsAmount <= 1 || !productsAvailableLabel || disabled) {
            return null;
        }

        const optionsText = String(productsAvailableLabel).replace('{0}', String(brickItemsAmount));

        return (
            <div className={styles.brick_list_options}>
                <span>{optionsText}</span>
            </div>
        )
    }
);

interface IBrickListOptions {
    brickItemsAmount: number;
    productsAvailableLabel: string;
    disabled?: boolean;
}

const BrickImage = React.memo(
    function BrickImage(props: IBrickImage) {
        const { disabled, imagePath, salesDesignation } = props;
        
        return (
            <img className={disabled ? styles.disabled : ''} src={imagePath} alt={salesDesignation} />
        )
    }
);

interface IBrickImage {
    disabled: boolean;
    imagePath: string;
    salesDesignation: string;
}

const Brick = inject('store')(observer((props: IBrickItemProps) =>  {
    const { store, brickPosition, disabled, index, detailsButtonLabel } = props;
    const brickSelection = store?.dataStore.orderModule.sortedBrickSelections[brickPosition];
    const localization = store?.localization;

    if (!brickSelection || !localization) {
        return null;
    }

    const setSelectedBrickNo= store!.orderModuleStore.setSelectedBrickNo;
    const setSelectedBrickPosition= store!.orderModuleStore.setSelectedBrickPosition;
    const isSelected = store?.orderModuleStore.selectedBrickNo === brickSelection.brickNo;
    const flexSetInfo = store!.dataStore.orderModule.flexSetInfo;

    const handleBrickItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        setSelectedBrickNo(isSelected ? null : brickSelection.brickNo);
        setSelectedBrickPosition(index);
    }

    const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;
    const imagePath = brickSelection.imageFullPath?.replace('Original', 'BrickList');
    const sku = brickSelection.salesArticleVariantKey ?
        String(brickSelection.salesArticleVariantKey).split('-')[0] : 'sold_out';

    return (
        <div data-testid='flexset-module-layer_0' data-semantic-id='flexset-module-layer_0'
             data-flexset-id={flexSetInfo.fsCode}
             data-flexset-name={flexSetInfo.displayName}
             data-flexset-module-layer_0-category={brickSelection.mainNavigationKeyPath}
             data-flexset-module-layer_0-index={index}
             data-flexset-item-name={brickSelection.salesDesignation}
             data-flexset-item-sku={sku}
             className={classNames(styles.brick_list_item,
                 disabled ? styles.disabled : '',
                 { [styles.selected]: isSelected }
             )}
             onClick={handleBrickItemClick}
             data-brickitem={brickSelection.selectedBrickItemNo}
             ref={(el) => tabNavigation.setFocusAtt(el)}
        >
            <div className={styles.brick_list_container}>
                <BrickImage
                    imagePath={imagePath}
                    disabled={disabled}
                    salesDesignation={brickSelection.salesDesignation}
                />
                <div className={styles.brick_list_content}>
                    { brickSelection.isFreeInSet &&
                        <div className={styles.free_in_set}>
                            {decodeHTML(localization.generalL10n.gratis)}
                        </div>
                    }
                    <div className={styles.top_row}>
                        <div className={classNames(styles.brick_list_text_content, disabled ? styles.disabled : '',)}>
                            <div className={classNames(styles.brick_list_title,
                                { [styles.has_multiple_options]: brickSelection.amountOfItems > 1})}
                            >
                                {brickSelection.includedQuantity > 1 ?
                                    brickSelection.includedQuantity + 'x ' + brickSelection.salesDesignation
                                    : brickSelection.salesDesignation}
                            </div>
                            <div>
                                {brickSelection.colorText &&
                                    <div className={styles.brick_list_label}>
                                        <span>{decodeHTML(localization.generalL10n.textColor)}:</span>
                                        <span className={styles.bs_text}>{
                                            brickSelection.colorText.split('/').join(' / ') ?? ''}
                                        </span>
                                    </div>
                                }
                                {brickSelection.sizeText &&
                                    <div className={styles.brick_list_label}>
                                        <span>{decodeHTML(localization.generalL10n.textSize)}:</span>
                                        <span className={styles.bs_text}>{
                                            brickSelection.sizeText.split('/').join(' / ') ?? ''}
                                        </span>
                                    </div>
                                }
                                {brickSelection.modelText &&
                                    <div className={styles.brick_list_label}>
                                        <span>{decodeHTML(localization.generalL10n.textModel)}:</span>
                                        <span className={
                                            styles.bs_text}>{brickSelection.modelText.split('/').join(' / ') ?? ''}
                                        </span>
                                    </div>
                                }
                            </div>
                        </div>
                        <BrickListOptions
                            brickItemsAmount={brickSelection.amountOfItems}
                            productsAvailableLabel={localization.generalL10n.productsAvailable}
                            disabled={disabled}
                        />
                    </div>

                    { !disabled &&
                        <div className={styles.bottom_row}>
                            <DetailsSelectButton isSelected={isSelected} detailsButtonLabel={detailsButtonLabel} />
                        </div>
                    }
                </div>
            </div>
            { !disabled &&
                <div className={styles.mobile_row}>
                    <DetailsSelectButton isSelected={isSelected} detailsButtonLabel={detailsButtonLabel} />
                    <BrickListOptions
                        brickItemsAmount={brickSelection.amountOfItems}
                        productsAvailableLabel={localization.generalL10n.productsAvailable}
                    />
                </div>
            }
        </div>
    );
}));

interface IBrickItemProps extends IInjectedProps{
    brickPosition: number;
    disabled: boolean;
    index: number;
    detailsButtonLabel: string;
}

const BrickList = inject('store')(
    observer(({ store }: IInjectedProps) => {
        const brickListRef = React.useRef<HTMLDivElement>(null);
        const sortedBrickSelection = store?.dataStore.orderModule.sortedBrickSelections;
        const sortedBrickItems = store?.dataStore.sortedBrickItems;


        when(
            () => store?.variantStore?.isVariantPanelOpen === false,
            () => {
                if (brickListRef.current) {
                    if (store?.variantStore?.selectedBrickItem.brickNo) {
                        const focusableElement = brickListRef.current.
                            querySelectorAll('[focusable]')[store?.variantStore?.selectedBrickItem.brickNo - 1];
                        window.shell.tabNav.focus(focusableElement);
                    }
                }
            }
        )

        if (!sortedBrickSelection || sortedBrickSelection.length === 0 || !sortedBrickItems) {
            return null;
        }


        // If at least on brick item is obsolete, disable all brick items
        const disableBricks = sortedBrickSelection.some((brickSelection) => {
            const brickItems = store?.dataStore.sortedBrickItems.filter(it =>
                it.brickNo === brickSelection?.brickNo
            );
            let obsoleteItemsInBrickCount = 0;
            brickItems.forEach((brickItem) => {
                if (brickItem.articleState === ArticleState.Obsoleted) {
                    obsoleteItemsInBrickCount = obsoleteItemsInBrickCount + 1;
                }
            });
            const allItemsInBrickAreSoldOut = obsoleteItemsInBrickCount === brickItems.length;
            return allItemsInBrickAreSoldOut;
        });

        return (
            <>
                <div>
                    <span className={styles.brick_list_info_text}>
                        {decodeHTML(store?.localization.generalL10n.selectFlexibleSet)}
                    </span>
                </div>
                <div className={styles.brick_list} ref={brickListRef}>
                    {sortedBrickSelection.map((brickSelection, index) => {
                        const hasChoice = brickSelection.amountOfItems > 1 && !disableBricks;
                        let onlyOptionHasVariants = false;

                        if (!hasChoice) {
                            const brickVariantSelection = sortedBrickItems.find(item =>
                                item.brickNo === brickSelection.brickNo)?.variantSelection;
                            if (brickVariantSelection) {
                                let hasMultipleSizeVariants = false;

                                brickVariantSelection.availableSizeTypes.forEach((sizeTypes) => {
                                    if (sizeTypes.sizes.length > 1) {
                                        hasMultipleSizeVariants = true;
                                        return;
                                    }
                                });

                                if (brickVariantSelection.availableColors.length > 1 ||
                                    brickVariantSelection.availableModels.length > 1 || hasMultipleSizeVariants)
                                {
                                    onlyOptionHasVariants = true;
                                }
                            }
                        }

                        let detailsButtonKey = store!.localization.generalL10n.chooseProductsAndVariant;
                        if (!hasChoice) {
                            if (!onlyOptionHasVariants) {
                                detailsButtonKey = store!.localization.generalL10n.showDetails;
                            } else {
                                detailsButtonKey = store!.localization.generalL10n.chooseVariant;
                            }
                        }
                        const detailsButtonLabel = decodeRtfText(detailsButtonKey);

                        return (
                            <Brick
                                key={`${brickSelection.brickNo}+bricklist`}
                                index={index} // for piwik
                                brickPosition={brickSelection.positionNo}
                                disabled={disableBricks}
                                detailsButtonLabel={detailsButtonLabel}
                            />
                        );
                    })}
                </div>
            </>
        );
    }));

export default BrickList;

